import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Mic, Upload, Play, Pause, Trash2, Download } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { formatDistanceToNow } from "date-fns";
import { de } from "date-fns/locale";
import {getAuthHeaders, hasToken, BACKEND_URL} from "@/lib/api";

const API = `${BACKEND_URL}/api`;

// Auth config helper
const authConfig = () => ({
  withCredentials: !hasToken(),
  headers: getAuthHeaders()
});

export default function EventAudioUpload({ eventId, isOwner }) {
  const [audios, setAudios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const fileInputRef = useRef(null);
  const audioRefs = useRef({});

  useEffect(() => {
    fetchAudios();
  }, [eventId]);

  const fetchAudios = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/audios`, authConfig());
      setAudios(response.data || []);
    } catch (error) {
      console.error("Failed to fetch audios:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (max 20MB)
    if (file.size > 20 * 1024 * 1024) {
      toast.error("Audio ist zu groß (max. 20MB)");
      return;
    }

    // Check file type
    if (!file.type.startsWith("audio/")) {
      toast.error("Bitte nur Audiodateien hochladen");
      return;
    }

    setUploading(true);

    const formData = new FormData();
    formData.append("audio", file);

    try {
      await axios.post(`${API}/events/${eventId}/audios`, formData, {
        ...authConfig(),
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" }
      });
      toast.success("Audio hochgeladen! 🎙️");
      fetchAudios();
    } catch (error) {
      toast.error("Fehler beim Hochladen");
    } finally {
      setUploading(false);
    }
  };

  const togglePlay = (audioId) => {
    const audio = audioRefs.current[audioId];
    if (!audio) return;

    if (playingId === audioId) {
      audio.pause();
      setPlayingId(null);
    } else {
      // Stop any currently playing audio
      if (playingId && audioRefs.current[playingId]) {
        audioRefs.current[playingId].pause();
      }
      audio.play();
      setPlayingId(audioId);
    }
  };

  const deleteAudio = async (audioId) => {
    try {
      await axios.delete(`${API}/events/${eventId}/audios/${audioId}`, authConfig());
      toast.success("Audio gelöscht");
      fetchAudios();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Mic className="w-5 h-5 text-green-500" />
          Audio hochladen
        </h3>
        
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*"
          onChange={handleUpload}
          className="hidden"
        />
        
        <Button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          variant="outline"
          className="w-full h-20 rounded-xl border-2 border-dashed border-gray-300 hover:border-green-400 hover:bg-green-50"
        >
          <div className="flex flex-col items-center gap-2">
            <Upload className="w-6 h-6 text-gray-400" />
            <span className="text-gray-600">
              {uploading ? "Wird hochgeladen..." : "Audio auswählen (max. 20MB)"}
            </span>
          </div>
        </Button>
      </div>

      {/* Audio List */}
      <div className="space-y-3">
        {audios.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Mic className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>Noch keine Audiodateien hochgeladen.</p>
          </div>
        ) : (
          audios.map((audio) => (
            <div key={audio.id} className="bg-white rounded-2xl p-4 shadow-sm flex items-center gap-4">
              <button
                onClick={() => togglePlay(audio.id)}
                className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                  playingId === audio.id ? "bg-green-500 text-white" : "bg-green-100 text-green-600"
                }`}
              >
                {playingId === audio.id ? (
                  <Pause className="w-5 h-5" />
                ) : (
                  <Play className="w-5 h-5 ml-1" />
                )}
              </button>
              
              <audio
                ref={(el) => audioRefs.current[audio.id] = el}
                src={`${BACKEND_URL}${audio.url}`}
                onEnded={() => setPlayingId(null)}
              />
              
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">{audio.name}</p>
                <p className="text-xs text-gray-400">
                  {formatDistanceToNow(new Date(audio.created_at), { addSuffix: true, locale: de })}
                </p>
              </div>
              
              <div className="flex gap-1">
                <a
                  href={`${BACKEND_URL}${audio.url}`}
                  download
                  className="p-2 text-gray-400 hover:text-green-500"
                >
                  <Download className="w-5 h-5" />
                </a>
                {isOwner && (
                  <button
                    onClick={() => deleteAudio(audio.id)}
                    className="p-2 text-gray-400 hover:text-red-500"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
